// Shared discount helpers — single source of truth for picking the active
// discount on a given plan and computing the final price. Used by:
//   • Landing → PricingSection.jsx (public, anonymous)
//   • PlanSelectionModal.jsx (first-login plan picker)
//   • Settings → MyPlanTab.jsx (existing subscriber upgrade flow)
//
// Backend lives at GET /api/plans which returns `{plans, discounts}` already
// filtered to `is_active=true` AND not-yet-expired. We still re-check both
// here in case a caller hands us a stale cache.

const isDiscountLive = (d) => {
  if (!d || !d.is_active) return false;
  const exp = d.expires_at;
  if (!exp) return true;
  try {
    return new Date(exp).getTime() > Date.now();
  } catch (_) {
    return true;
  }
};

// Billing cycles a discount is eligible for; legacy records → both.
const cyclesOf = (d) =>
  Array.isArray(d?.billing_cycles) && d.billing_cycles.length
    ? d.billing_cycles
    : ["monthly", "annual"];

export const getActiveDiscount = (plan, discounts, cycle = "monthly") => {
  if (!plan || !discounts || discounts.length === 0) return null;
  return (
    discounts.find((d) => {
      if (!isDiscountLive(d)) return false;
      if (!cyclesOf(d).includes(cycle)) return false;
      if (d.applies_to === "all") return true;
      return (d.plan_ids || []).includes(plan.plan_id);
    }) || null
  );
};

export const applyDiscount = (price, discount) => {
  if (!discount) return price;
  if (discount.discount_type === "percentage") {
    return Math.round(price * (1 - (discount.value || 0) / 100));
  }
  return Math.max(0, Math.round(price - (discount.value || 0)));
};

// Base annual pricing = monthly × 12 × 0.80 (the standard 20% annual rate,
// matching backend checkout + Stripe synced annual prices).
export const ANNUAL_MULTIPLIER = 0.8;

// Annual pricing with the discount applied to the ANNUAL INVOICE AMOUNT
// (fixed $ comes off the yearly invoice, not the monthly price) — this
// mirrors exactly how the backend attaches the Stripe coupon at checkout.
export const computeAnnualPricing = (plan, discounts) => {
  const annualBase = Math.round(Number(plan?.price || 0) * 12 * ANNUAL_MULTIPLIER);
  const discount = getActiveDiscount(plan, discounts, "annual");
  let finalAnnual = annualBase;
  if (discount) {
    finalAnnual =
      discount.discount_type === "percentage"
        ? Math.round(annualBase * (1 - (discount.value || 0) / 100))
        : Math.max(0, Math.round(annualBase - (discount.value || 0)));
  }
  return {
    annualBase,
    finalAnnual,
    discount,
    hasDiscount: !!discount && finalAnnual < annualBase,
  };
};

// Human-readable renewal terms for annual billing with a discount.
export const annualRenewalNote = (discount, annualBase, finalAnnual) => {
  if (!discount || finalAnnual >= annualBase) return `Renews at $${annualBase}/yr`;
  const ad = discount.annual_duration;
  if (ad === "first" || (!ad && discount.duration === "once")) {
    return `Discount applies to your first annual payment — renews at $${annualBase}/yr`;
  }
  if (ad === "periods" || (!ad && discount.duration === "repeating")) {
    const n =
      ad === "periods"
        ? Math.max(1, discount.annual_periods || 1)
        : Math.max(1, Math.ceil((discount.duration_months || 12) / 12));
    return `Discount applies to your first ${n} annual payment${n > 1 ? "s" : ""} — then $${annualBase}/yr`;
  }
  return `Renews at $${finalAnnual}/yr with discount applied`;
};

// Convenience: returns {basePrice, finalPrice, hasDiscount, discount} for a
// given plan + discounts list. Callers can destructure for rendering.
export const computePlanPricing = (plan, discounts, cycle = "monthly") => {
  const basePrice = Number(plan?.price || 0);
  const discount = getActiveDiscount(plan, discounts, cycle);
  const finalPrice = discount ? applyDiscount(basePrice, discount) : basePrice;
  return {
    basePrice,
    finalPrice,
    hasDiscount: !!discount && finalPrice < basePrice,
    discount,
  };
};
